import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * TBO audit log (admin-only viewer). Read models over the `tbo_api_call`
 * table populated by the non-blocking recorder. The heavy request/response
 * bodies live in S3; the list row only signals their presence (`hasBody`)
 * and the body is fetched on demand via a short-lived presigned URL.
 */

// ----- Enums (mirror the Prisma enums) -----

export const TboAuditProviderSchema = z.enum(['TBO_FLIGHT', 'TBO_HOTEL']);
export type TboAuditProvider = z.infer<typeof TboAuditProviderSchema>;

export const TboAuditOperationSchema = z.enum([
  'SEARCH',
  'FARE_RULE',
  'FARE_QUOTE',
  'PRE_BOOK',
  'BOOK',
  'HOTEL_SEARCH',
  'HOTEL_ROOM',
  'HOTEL_BOOK',
  'AUTHENTICATE',
  'OTHER',
]);
export type TboAuditOperation = z.infer<typeof TboAuditOperationSchema>;

export const TboAuditSourceSchema = z.enum(['MANUAL_PICKER', 'AUTOFILL', 'REPRICE', 'SYSTEM']);
export type TboAuditSource = z.infer<typeof TboAuditSourceSchema>;

export const TboAuditOutcomeSchema = z.enum([
  'SUCCESS',
  'NO_RESULTS',
  'TBO_ERROR',
  'HTTP_ERROR',
  'TIMEOUT',
]);
export type TboAuditOutcome = z.infer<typeof TboAuditOutcomeSchema>;

// ----- List query -----

export const TboAuditListQuerySchema = PaginationQuerySchema.extend({
  userId: UuidSchema.optional(),
  leadId: UuidSchema.optional(),
  itineraryId: UuidSchema.optional(),
  provider: TboAuditProviderSchema.optional(),
  operation: TboAuditOperationSchema.optional(),
  outcome: TboAuditOutcomeSchema.optional(),
  /** Inclusive lower bound on createdAt (ISO datetime). */
  from: IsoDateTimeSchema.optional(),
  /** Inclusive upper bound on createdAt (ISO datetime). */
  to: IsoDateTimeSchema.optional(),
});
export type TboAuditListQuery = z.infer<typeof TboAuditListQuerySchema>;

// ----- Row DTO (heavy bodies excluded; `hasBody` flags S3 presence) -----

export const TboAuditRowSchema = z.object({
  /** BigInt id serialised as a decimal string. */
  id: z.string(),
  provider: TboAuditProviderSchema,
  operation: TboAuditOperationSchema,
  source: TboAuditSourceSchema,
  userId: UuidSchema.nullable(),
  /**
   * Resolved effective lead = the audit row's own `leadId` when present, else
   * the lead of the row's itinerary (`row.leadId ?? itinerary.leadId`). This
   * lets the UI link to the lead even when the audit row itself never captured
   * a leadId but its itinerary has one.
   */
  leadId: UuidSchema.nullable(),
  itineraryId: UuidSchema.nullable(),
  /** Resolved user display name (`User.fullName`), null when unresolved. */
  userName: z.string().nullable(),
  /** Resolved itinerary title (`Itinerary.title`), null when unresolved. */
  itineraryTitle: z.string().nullable(),
  /** Resolved lead reference (`Lead.reference`) for the effective leadId. */
  leadReference: z.string().nullable(),
  requestId: z.string().nullable(),
  /** Small JSON gist (route/cabin/pax etc.) - not the full body. */
  summary: z.unknown().nullable(),
  httpStatus: z.number().int().nullable(),
  tboResponseStatus: z.number().int().nullable(),
  tboErrorCode: z.number().int().nullable(),
  tboErrorMessage: z.string().nullable(),
  traceId: z.string().nullable(),
  resultCount: z.number().int().nullable(),
  requestBytes: z.number().int().nullable(),
  responseBytes: z.number().int().nullable(),
  latencyMs: z.number().int().nullable(),
  outcome: TboAuditOutcomeSchema.nullable(),
  /** True when a gzipped request/response body is stored in S3. */
  hasBody: z.boolean(),
  createdAt: IsoDateTimeSchema,
});
export type TboAuditRow = z.infer<typeof TboAuditRowSchema>;

export const TboAuditListResponseSchema = paginatedResponseSchema(TboAuditRowSchema);
export type TboAuditListResponse = z.infer<typeof TboAuditListResponseSchema>;

export const TboAuditBodyUrlResponseSchema = z.object({
  /** Short-lived presigned GET URL for the stored body. */
  url: z.string(),
});
export type TboAuditBodyUrlResponse = z.infer<typeof TboAuditBodyUrlResponseSchema>;
